import { useState, useEffect } from 'react';

const HeaderSettingsEmbedded = ({
    widgetConfig,
    onWidgetConfigChange
}) => {
    // Initialize configurations
    const initializeHeaderConfig = () => {
        const currentConfig = widgetConfig?.headerConfig || {};
        return {
            showHeader: currentConfig.showHeader !== undefined ? currentConfig.showHeader : true,
            elements: {
                heading: currentConfig.elements?.heading !== undefined ? currentConfig.elements.heading : true,
                rating: currentConfig.elements?.rating !== undefined ? currentConfig.elements.rating : true,
                numberOfReviews: currentConfig.elements?.numberOfReviews !== undefined ? currentConfig.elements.numberOfReviews : true,
                writeReviewButton: currentConfig.elements?.writeReviewButton !== undefined ? currentConfig.elements.writeReviewButton : true,
            }
        };
    };

    const initializeTabsConfig = () => {
        const currentConfig = widgetConfig?.tabsConfig || {};
        return {
            showTabs: currentConfig.showTabs !== undefined ? currentConfig.showTabs : true,
            elements: {
                icon: currentConfig.elements?.icon !== undefined ? currentConfig.elements.icon : true,
                name: currentConfig.elements?.name !== undefined ? currentConfig.elements.name : true,
                rating: currentConfig.elements?.rating !== undefined ? currentConfig.elements.rating : true,
            }
        };
    };

    const initializeWidgetTitleConfig = () => {
        const currentConfig = widgetConfig?.widgetTitleConfig || {};
        return {
            showTitle: currentConfig.showTitle !== undefined ? currentConfig.showTitle : true,
            title: currentConfig.title || "What our customers say",
            caption: currentConfig.caption || "Hello this is just a demo"
        };
    };

    const [headerConfig, setHeaderConfig] = useState(initializeHeaderConfig);
    const [tabsConfig, setTabsConfig] = useState(initializeTabsConfig);
    const [widgetTitleConfig, setWidgetTitleConfig] = useState(initializeWidgetTitleConfig);

    // Update component state when widgetConfig prop changes (for edit mode)
    useEffect(() => {
        const newHeaderConfig = widgetConfig?.headerConfig || {
            showHeader: true,
            elements: {
                heading: true,
                rating: true,
                numberOfReviews: true,
                writeReviewButton: true,
            }
        };
        setHeaderConfig(newHeaderConfig);
    }, [widgetConfig?.headerConfig]);

    useEffect(() => {
        const newTabsConfig = widgetConfig?.tabsConfig || {
            showTabs: true,
            elements: {
                icon: true,
                name: true,
                rating: true,
            }
        };
        setTabsConfig(newTabsConfig);
    }, [widgetConfig?.tabsConfig]);

    useEffect(() => {
        const newWidgetTitleConfig = widgetConfig?.widgetTitleConfig || {
            showTitle: true,
            title: "What our customers say",
            caption: "Hello this is just a demo"
        };
        setWidgetTitleConfig(newWidgetTitleConfig);
    }, [widgetConfig?.widgetTitleConfig]);

    // Update widget config
    const updateWidgetConfig = (newHeaderConfig, newTabsConfig, newWidgetTitleConfig) => {
        const updatedConfig = {
            ...widgetConfig,
            headerConfig: newHeaderConfig || headerConfig,
            tabsConfig: newTabsConfig || tabsConfig,
            widgetTitleConfig: newWidgetTitleConfig || widgetTitleConfig
        };
        onWidgetConfigChange(updatedConfig);
    };

    // Toggle Switch Component
    const ToggleSwitch = ({ enabled, onChange, disabled = false }) => (
        <div
            className={`w-11 h-6 rounded-full relative cursor-pointer transition-colors duration-200 ${enabled ? 'bg-blue-500' : 'bg-gray-600'
                } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
            onClick={() => !disabled && onChange(!enabled)}
        >
            <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform duration-200 ${enabled ? 'transform translate-x-5' : 'translate-x-0.5'
                }`}></div>
        </div>
    );

    // Checkbox Component
    const Checkbox = ({ checked, onChange, disabled = false }) => (
        <div
            className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors duration-200 ${checked
                ? 'bg-blue-500 border-blue-500'
                : 'border-gray-500 bg-transparent'
                } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
            onClick={() => !disabled && onChange(!checked)}
        >
            {checked && <span className="text-white text-xs font-bold">✓</span>}
        </div>
    );

    // Handle changes
    const handleHeaderChange = (field, value) => {
        const newConfig = { ...headerConfig };
        if (field === 'showHeader') {
            newConfig.showHeader = value;
        } else {
            newConfig.elements[field] = value;
        }
        setHeaderConfig(newConfig);
        updateWidgetConfig(newConfig, null, null);
    };

    const handleTabsChange = (field, value) => {
        const newConfig = { ...tabsConfig };
        if (field === 'showTabs') {
            newConfig[field] = value;
        } else {
            newConfig.elements[field] = value;
        }
        setTabsConfig(newConfig);
        updateWidgetConfig(null, newConfig, null);
    };

    const handleWidgetTitleChange = (field, value) => {
        const newConfig = { ...widgetTitleConfig, [field]: value };
        setWidgetTitleConfig(newConfig);
        updateWidgetConfig(null, null, newConfig);
    };

    return (
        <div className="p-4 space-y-6 h-full overflow-y-auto bg-gray-900">
            <div className="mb-4">
                <h3 className="text-lg font-semibold text-white mb-2">Header Configuration</h3>
                <p className="text-gray-400 text-sm">Configure how your widget header and title appear</p>
            </div>

            {/* Header Section */}
            <div className="bg-gray-800 rounded-lg p-4">
                <div className="text-md font-semibold mb-4 text-white">Header</div>

                <div className="space-y-4">
                    <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-300">Show Header</span>
                        <ToggleSwitch
                            enabled={headerConfig.showHeader}
                            onChange={(value) => handleHeaderChange('showHeader', value)}
                        />
                    </div>

                    <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-300">Heading</span>
                        <Checkbox
                            checked={headerConfig.elements.heading}
                            onChange={(value) => handleHeaderChange('heading', value)}
                            disabled={!headerConfig.showHeader}
                        />
                    </div>

                    <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-300">Rating</span>
                        <Checkbox
                            checked={headerConfig.elements.rating}
                            onChange={(value) => handleHeaderChange('rating', value)}
                            disabled={!headerConfig.showHeader}
                        />
                    </div>

                    <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-300">Number of Reviews</span>
                        <Checkbox
                            checked={headerConfig.elements.numberOfReviews}
                            onChange={(value) => handleHeaderChange('numberOfReviews', value)}
                            disabled={!headerConfig.showHeader}
                        />
                    </div>

                    <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-300">Write a Review Button</span>
                        <Checkbox
                            checked={headerConfig.elements.writeReviewButton}
                            onChange={(value) => handleHeaderChange('writeReviewButton', value)}
                            disabled={!headerConfig.showHeader}
                        />
                    </div>
                </div>
            </div>

            {/* Tabs Section */}
            <div className="bg-gray-800 rounded-lg p-4">
                <div className="text-md font-semibold mb-4 text-white">Tabs</div>

                <div className="space-y-4">
                    <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-300">Show Tabs</span>
                        <ToggleSwitch
                            enabled={tabsConfig.showTabs}
                            onChange={(value) => handleTabsChange('showTabs', value)}
                        />
                    </div>

                    <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-300">Icon</span>
                        <Checkbox
                            checked={tabsConfig.elements.icon}
                            onChange={(value) => handleTabsChange('icon', value)}
                            disabled={!tabsConfig.showTabs}
                        />
                    </div>

                    <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-300">Name</span>
                        <Checkbox
                            checked={tabsConfig.elements.name}
                            onChange={(value) => handleTabsChange('name', value)}
                            disabled={!tabsConfig.showTabs}
                        />
                    </div>

                    <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-300">Rating</span>
                        <Checkbox
                            checked={tabsConfig.elements.rating}
                            onChange={(value) => handleTabsChange('rating', value)}
                            disabled={!tabsConfig.showTabs}
                        />
                    </div>
                </div>
            </div>

            {/* Widget Title Section */}
            <div className="bg-gray-800 rounded-lg p-4">
                <div className="text-md font-semibold mb-4 text-white">Widget Title</div>

                <div className="space-y-4">
                    <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-gray-300">Widget Title</span>
                        <ToggleSwitch
                            enabled={widgetTitleConfig.showTitle}
                            onChange={(value) => handleWidgetTitleChange('showTitle', value)}
                        />
                    </div>

                    <div>
                        <label className="block mb-2 text-sm font-medium text-gray-300">Title</label>
                        <input
                            type="text"
                            className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors duration-200"
                            value={widgetTitleConfig.title}
                            onChange={(e) => handleWidgetTitleChange('title', e.target.value)}
                            disabled={!widgetTitleConfig.showTitle}
                            placeholder="Enter widget title"
                        />
                    </div>

                    <div>
                        <label className="block mb-2 text-sm font-medium text-gray-300">Caption</label>
                        <textarea
                            className="w-full bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors duration-200 resize-vertical"
                            rows="3"
                            value={widgetTitleConfig.caption}
                            onChange={(e) => handleWidgetTitleChange('caption', e.target.value)}
                            disabled={!widgetTitleConfig.showTitle}
                            placeholder="Enter widget caption"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HeaderSettingsEmbedded; 